import React, { useState, useEffect, useRef } from "react";
import {
  Bell,
  X,
  Check,
  CheckCheck,
  Calendar,
  User,
  Phone,
} from "lucide-react";
import { format } from "date-fns";
import { notificationsAPI } from "../services/api";

const Notifications = ({ onCountUpdate }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasNewNotification, setHasNewNotification] = useState(false);
  const dropdownRef = useRef(null);
  const previousCountRef = useRef(0);
  const audioRef = useRef(null);

  useEffect(() => {
    // Create audio element for notification sound
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBVXM/fX2xn0wBCF+0O/XkDUICWKz7OucTRALU6fm8bNfGgU7ldnywHkpBSh+zO/aizsKE2S47emiUhIKSKHh8bllHAU9mdvyyHUpBSh+zO/ajTsKE2O37emhUhEKRp/f8btmHwY9mdryxnwrBSZ9yu7Ziz0KB2O26+edURIMTqbk8bNeGgU8mNjyxn4yBil9zO/ajz4KB2Ky7OqfURIMTKPi8L5kHwU/nN7wxHUrBSp7yu7YizsKDl6v6uybTQ8NSZ/d77pkHgU9mtryx34wBSl7yu7XizsKEWCy7eqgUxELTaXl8bdiHQU9ntvzx4EvBil7yu7XizsKEWCy7eqgUxELTaXl8bdiHQU9ntvzx4EvBSh6yu7WizsJEV+y7uqgUhALTKTj8bZhHQU8ndryx38xBSh6yu7WizoJD1+u7OmfURAKSqDf8bllHgU7nNnyxoAyBSh6yu7VijoJDl6t6+ieUBAKSZ/e8bllHgU6nNjyxoAzBCd5ye7ViToIDl6s6+mdUBAKSZ/e8bllHgU6nNjyxoAzBCd5ye7ViToIDl6s6+mdUBAKSZ/e8bllHgU6nNjyxoAzBCd5ye7ViToIDl6s6+mdUBAKSZ/e8bllHgU6nNjyxoAzBCd5ye7ViToIDl6s6+mdUBAKSZ/e8bllHgU6nNjyxoAzBCd5ye7ViToIDl6s6+mdUBAKSZ/e8bllHgU6nNjyxoAzBCd5ye7ViToIDl6s6+mdUBAKSZ/e8bllHgU6nNjyxoAzBCd5ye7ViToIDl6s6+mdUBAKSZ/e8bllHgU6nNjyxoAzBCd5ye7ViToIDl6s6+mdTxAK');
    
    fetchUnreadCount();

    // Poll for new notifications every 5 seconds (more frequent)
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (showDropdown) {
      fetchNotifications();
      setHasNewNotification(false);
    }
  }, [showDropdown]);

  useEffect(() => {
    // Update parent component when count changes
    if (onCountUpdate) {
      onCountUpdate(unreadCount);
    }

    // Check if count increased (new notification)
    if (unreadCount > previousCountRef.current && previousCountRef.current !== 0) {
      setHasNewNotification(true);
      playNotificationSound();
      showBrowserNotification();
      
      // Auto-hide the bell animation after 3 seconds
      setTimeout(() => {
        setHasNewNotification(false);
      }, 3000);
    }
    
    previousCountRef.current = unreadCount;
  }, [unreadCount, onCountUpdate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Request browser notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  const playNotificationSound = () => {
    try {
      if (audioRef.current) {
        audioRef.current.play().catch(() => {});
      }
    } catch {
      // Browser blocked autoplay — silently ignore
    }
  };

  const showBrowserNotification = () => {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("New Notification", {
        body: "You have a new notification in Customer Report System",
        icon: "/favicon.ico",
        badge: "/favicon.ico",
        tag: "customer-report-notification",
        requireInteraction: false,
      });
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationsAPI.getAll();
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationsAPI.getUnreadCount();
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      // Remove from local state
      setNotifications(notifications.filter((n) => n.id !== notificationId));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      // Refresh to ensure sync
      fetchUnreadCount();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications([]);
      setUnreadCount(0);
      setHasNewNotification(false);
      // Refresh to ensure sync
      fetchUnreadCount();
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await notificationsAPI.delete(notificationId);
      // Remove from local state
      setNotifications(notifications.filter((n) => n.id !== notificationId));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      // Refresh to ensure sync
      fetchUnreadCount();
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "interaction_reminder":
        return React.createElement(Calendar, { className: "w-5 h-5 text-blue-500" });
      case "order_reminder":
        return React.createElement(Phone, { className: "w-5 h-5 text-green-500" });
      default:
        return React.createElement(User, { className: "w-5 h-5 text-gray-500" });
    }
  };

  const formatNotificationTime = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch (error) {
      return dateString;
    }
  };

  return React.createElement(
    "div",
    { className: "relative", ref: dropdownRef },
    React.createElement(
      "button",
      {
        onClick: () => setShowDropdown(!showDropdown),
        className: `relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all ${
          hasNewNotification ? "animate-bounce" : ""
        }`
      },
      React.createElement(Bell, {
        className: `w-6 h-6 transition-all ${
          hasNewNotification ? "text-blue-600 animate-pulse" : ""
        }`
      }),
      hasNewNotification &&
        React.createElement("span", {
          className: "absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full animate-ping"
        })
    ),
    showDropdown &&
      React.createElement(
        React.Fragment,
        null,
        React.createElement("div", {
          className: "fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden",
          onClick: () => setShowDropdown(false)
        }),
        React.createElement(
          "div",
          {
            className: "fixed inset-x-0 bottom-0 lg:absolute lg:right-0 lg:left-auto lg:bottom-auto lg:top-full lg:mt-2 w-full lg:w-96 bg-white rounded-t-2xl lg:rounded-lg shadow-xl border-t lg:border border-gray-200 z-50 animate-fadeIn max-h-[85vh] lg:max-h-[600px] flex flex-col"
          },
          React.createElement(
            "div",
            { className: "flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0" },
            React.createElement(
              "h3",
              { className: "text-lg font-semibold text-gray-900" },
              "Notifications"
            ),
            React.createElement(
              "div",
              { className: "flex items-center gap-2" },
              notifications.length > 0 &&
                React.createElement(
                  "button",
                  {
                    onClick: markAllAsRead,
                    className: "text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                  },
                  React.createElement(CheckCheck, { className: "w-3 h-3 sm:w-4 sm:h-4" }),
                  React.createElement("span", { className: "hidden sm:inline" }, "Mark all read"),
                  React.createElement("span", { className: "sm:hidden" }, "Read all")
                ),
              React.createElement(
                "button",
                {
                  onClick: () => setShowDropdown(false),
                  className: "lg:hidden p-1 text-gray-400 hover:text-gray-600"
                },
                React.createElement(X, { className: "w-5 h-5" })
              )
            )
          ),
          React.createElement(
            "div",
            { className: "overflow-y-auto flex-1" },
            loading
              ? React.createElement(
                  "div",
                  { className: "flex items-center justify-center p-8" },
                  React.createElement("div", { className: "loading-spinner" })
                )
              : notifications.length === 0
              ? React.createElement(
                  "div",
                  { className: "p-8 text-center" },
                  React.createElement(Bell, { className: "w-12 h-12 text-gray-300 mx-auto mb-3" }),
                  React.createElement("p", { className: "text-sm sm:text-base text-gray-500" }, "No new notifications")
                )
              : React.createElement(
                  "div",
                  { className: "divide-y divide-gray-100" },
                  notifications.map((notification) =>
                    React.createElement(
                      "div",
                      {
                        key: notification.id,
                        className: "p-3 sm:p-4 hover:bg-gray-50 transition-colors animate-slideIn"
                      },
                      React.createElement(
                        "div",
                        { className: "flex items-start gap-2 sm:gap-3" },
                        React.createElement(
                          "div",
                          { className: "flex-shrink-0 mt-1" },
                          getNotificationIcon(notification.type)
                        ),
                        React.createElement(
                          "div",
                          { className: "flex-1 min-w-0" },
                          React.createElement(
                            "p",
                            { className: "text-sm font-medium text-gray-900 break-words" },
                            notification.title
                          ),
                          React.createElement(
                            "p",
                            { className: "text-xs sm:text-sm text-gray-600 mt-1 break-words" },
                            notification.message
                          ),
                          notification.customer_name &&
                            React.createElement(
                              "p",
                              { className: "text-xs text-gray-500 mt-1 truncate" },
                              "Customer: " + notification.customer_name
                            ),
                          React.createElement(
                            "p",
                            { className: "text-xs text-gray-400 mt-1" },
                            formatNotificationTime(notification.created_at)
                          )
                        ),
                        React.createElement(
                          "div",
                          { className: "flex-shrink-0 flex gap-1" },
                          React.createElement(
                            "button",
                            {
                              onClick: () => markAsRead(notification.id),
                              className: "p-1 text-gray-400 hover:text-green-600 transition-colors touch-manipulation",
                              title: "Mark as read"
                            },
                            React.createElement(Check, { className: "w-4 h-4" })
                          ),
                          React.createElement(
                            "button",
                            {
                              onClick: () => deleteNotification(notification.id),
                              className: "p-1 text-gray-400 hover:text-red-600 transition-colors touch-manipulation",
                              title: "Delete"
                            },
                            React.createElement(X, { className: "w-4 h-4" })
                          )
                        )
                      )
                    )
                  )
                )
          )
        )
      )
  );
};

export default Notifications;